import { Info, Plug } from '@orgus/ui/icons';
import { Button } from '@orgus/ui';
import { startData } from '../../lib/data.ts';
import { setMockPreference } from '../../lib/mock.ts';
import { useConnection } from '../../lib/store.ts';
import { CONNECTION_LABEL } from '../../lib/stream.ts';
import type { ConnectionStatus } from '../../lib/stream.ts';
import type { Tone } from '@orgus/ui';
import { formatRelative } from '@orgus/ui';
import { useNow } from '@orgus/ui';
import { cn } from '@orgus/ui';
import { Spinner } from '@orgus/ui';
import { StatusDot } from '@orgus/ui';
import { Tooltip } from '@orgus/ui';

/**
 * The dot tone for each live state. Demo data is not a connection state: it gets the neutral
 * info treatment (Info icon, bold label), never a status colour and never the accent, which
 * means action. The status page shows demo data the same way.
 */
export const CONNECTION_TONE: Record<Exclude<ConnectionStatus, 'mock'>, Tone> = {
  connecting: 'idle',
  live: 'passed',
  reconnecting: 'waiting',
  offline: 'failed',
  'hub-error': 'failed',
  'signed-out': 'waiting',
};

const DETAIL: Record<ConnectionStatus, string> = {
  connecting: 'Opening the live stream to the hub.',
  live: 'Changes appear as they happen.',
  reconnecting: 'The stream dropped. Retrying; what you see may be out of date.',
  offline: 'The hub is not answering. Retrying every few seconds; what you see is out of date.',
  'hub-error': 'The hub answers, but with a server error. Retrying; what you see is out of date. The hub terminal has the details.',
  'signed-out': 'The hub no longer accepts this browser. Sign in again to see live data.',
  mock: 'Demo data from a simulator. Nothing here is real. Connect to hub leaves demo mode for this tab.',
};

/**
 * Leave demo mode for this tab. The choice is kept per tab (isMockRequested), so removing
 * ?mock=1 from the address alone never brought live data back (21 Oct 2026).
 */
export function connectToHub(): void {
  setMockPreference(false);
  startData({ mock: false });
}

/** The live connection pill in the top bar; in demo mode, with the way back to the hub beside it. */
export function ConnectionIndicator() {
  const { connection, lastMessageAt } = useConnection();
  const now = useNow(5000);
  return <ConnectionPill connection={connection} lastMessageAt={lastMessageAt} now={now} />;
}

/** The pill itself, from the connection state (tested without the store). */
export function ConnectionPill({ connection, lastMessageAt, now }: { connection: ConnectionStatus; lastMessageAt: number | null; now: number }) {
  const busy = connection === 'connecting' || connection === 'reconnecting';
  const tip = (
    <span className="flex flex-col gap-0.5">
      <span>{DETAIL[connection]}</span>
      {lastMessageAt != null && connection !== 'mock' && (
        <span className="text-fg-subtle">Last update {formatRelative(lastMessageAt, now)}</span>
      )}
    </span>
  );
  const pill = (
    <Tooltip content={tip} side="bottom" align="end">
      <span
        tabIndex={0}
        role="status"
        aria-live="polite"
        className={cn(
          'inline-flex h-control-sm shrink-0 items-center gap-2 rounded-pill px-2 text-caption font-medium',
          connection === 'offline' || connection === 'hub-error'
            ? 'text-failed'
            : connection === 'reconnecting' || connection === 'signed-out'
              ? 'text-waiting'
              : connection === 'mock'
                ? 'text-fg-muted'
                : 'text-fg-muted',
        )}
      >
        {busy ? (
          <Spinner size={11} />
        ) : connection === 'mock' ? (
          <Info className="size-3.5 shrink-0 text-fg-muted" aria-hidden />
        ) : (
          <StatusDot tone={CONNECTION_TONE[connection]} pulse={connection === 'live'} size="xs" label="" />
        )}
        {/* The word shows from 1280 px; below that the icon and the tooltip carry it (and screen readers read it). */}
        <span className="sr-only xl:not-sr-only">{CONNECTION_LABEL[connection]}</span>
      </span>
    </Tooltip>
  );
  if (connection !== 'mock') return pill;
  return (
    <span className="inline-flex shrink-0 items-center gap-1">
      {pill}
      <Button variant="ghost" size="sm" icon={<Plug />} onClick={connectToHub}>
        Connect to hub
      </Button>
    </span>
  );
}
